import React, { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { Map, YMaps, SearchControl, ZoomControl, ObjectManager } from "@pbe/react-yandex-maps";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { YANDEX_MAPS_DATA } from "../../../constants";
import { useLazyGetFacilitiesMapElementsInfoQuery } from "../../../middleware/facilitiesApi";
import { getCustomPlacemark } from "../../components/DonurChartMarker/indxex";
import MapPopUp from "../../components/MapPopUp";
import { PATHS } from "../../../constants";
import { useAppSelector } from "../../../hooks/redux";
import SetNewPasswordDialog from "../Profile/SetNewPasswordDialog";
import ObjectsHeader from "../ObjectsHeader";
import { House } from "./helpers";
import Analitics from "./Analitics";
import "./styles/styles.css";

const COLORS = ["#F8F9FD", "#F8F9FD", "#BFCCDB", "#9CA9BD", "#A4A8FC"];

// Дефолтное состояние карты
const DEFAULT_MAP_STATE = {
  center: [55.751244, 37.618423], // Центр России (Москва)
  zoom: 4,
};

const ObjectsOnMap = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [isMapPopUpOpen, setIsMapPopUpOpen] = useState(false);
  const [selectedMarkerID, setSelectedMarkerId] = useState("");
  const [facilitiesMapElements, setFacilitiesMapElements] = useState([]);
  const token = useAppSelector((state) => state.users.token);
  const isLoggedBefore = localStorage.getItem("isLoggedBefore");
  const [passwordDialog, setPasswordDialog] = useState(isLoggedBefore === "false" ? false : true);
  const handlePasswordDialog = () => setPasswordDialog(!passwordDialog);

  const [mapState, setMapState] = useState(DEFAULT_MAP_STATE);
  const [hasDataLoaded, setHasDataLoaded] = useState(false);

  const [getFacilitiesMapElementsInfo] = useLazyGetFacilitiesMapElementsInfoQuery();

  useEffect(() => {
    (async function () {
      try {
        const data = await getFacilitiesMapElementsInfo();
        const filterData = data?.data?.filter((item) => item?.coordinates !== null) || [];
        setFacilitiesMapElements(filterData);
        setHasDataLoaded(true);

        if (filterData.length > 0) {
          const bounds = getMapBounds(filterData);
          const newMapState = calculateMapState(bounds);
          setMapState(newMapState);
        }
      } catch (error) {
        console.error("Ошибка загрузки данных карты:", error);
        setHasDataLoaded(true); // Помечаем, что данные загружены (даже если пустые)
      }
    })();
  }, [getFacilitiesMapElementsInfo]);

  const popUpHandler = useCallback(
    (id) => {
      setSelectedMarkerId(id);
      setIsMapPopUpOpen(true);
    },
    [setSelectedMarkerId]
  );

  function getMapBounds(data) {
    const bounds = data.reduce(
      (acc, obj) => {
        if (obj?.coordinates) {
          return [
            [Math.min(acc[0][0], obj?.coordinates?.latitude), Math.min(acc[0][1], obj?.coordinates?.longitude)],
            [Math.max(acc[1][0], obj?.coordinates?.latitude), Math.max(acc[1][1], obj?.coordinates?.longitude)],
          ];
        } else {
          return acc;
        }
      },
      [
        [Infinity, Infinity],
        [-Infinity, -Infinity],
      ]
    );
    return bounds;
  }

  function calculateMapState(bounds) {
    const [[minLat, minLon], [maxLat, maxLon]] = bounds;

    // Проверяем, что границы валидны (не остались значения Infinity)
    if (minLat === Infinity || minLon === Infinity) {
      return DEFAULT_MAP_STATE;
    }

    const center = [(minLat + maxLat) / 2, (minLon + maxLon) / 2];
    const isSingleMarker = minLat === maxLat && minLon === maxLon;
    let zoom;

    if (isSingleMarker) {
      zoom = 12;
    } else {
      const deltaLat = Math.abs(maxLat - minLat);
      const deltaLon = Math.abs(maxLon - minLon);
      const maxDelta = Math.max(deltaLat, deltaLon);
      const coefficient = maxDelta <= 6 ? 0 : maxDelta > 11 ? 2 : 1;

      zoom = Math.floor(Math.log2(360 / maxDelta)) + coefficient;
    }
    return {
      center,
      zoom,
    };
  }

  function getRegions(ymaps) {
    if (mapRef && mapRef.current) {
      let objectManager = new ymaps.ObjectManager();
      ymaps.borders
        .load("RU", {
          lang: "ru",
          quality: 3,
        })
        .then(function (result) {
          let queue = [];
          let regions = result.features?.reduce(function (acc, feature) {
            let iso = feature.properties.iso3166;
            feature.id = iso;
            feature.options = {
              fillOpacity: 0.5,
              strokeColor: "#808080",
              strokeOpacity: 0.5,
            };
            acc[iso] = feature;
            return acc;
          }, {});

          function paint(iso) {
            let allowedColors = COLORS.slice();
            let region = regions[iso];
            let neighbors = region.properties.neighbors;

            if (region.options.fillColor) {
              return;
            }

            if (neighbors.length !== 0) {
              neighbors.forEach(function (neighbor) {
                const fillColor = regions[neighbor].options.fillColor;
                if (fillColor) {
                  let index = allowedColors.indexOf(fillColor);
                  if (index !== -1) {
                    allowedColors.splice(index, 1);
                  }
                } else if (queue.indexOf(neighbor) === -1) {
                  queue.push(neighbor);
                }
              });
            }
            region.options.fillColor = allowedColors[0];
          }

          for (let iso in regions) {
            if (!regions[iso].options.fillColor) {
              queue.push(iso);
            }
            while (queue.length > 0) {
              paint(queue.shift());
            }
          }

          result.features = [];
          for (let reg in regions) {
            result.features.push(regions[reg]);
          }

          objectManager.add(result);
          mapRef.current.geoObjects.add(objectManager);
        })
        .catch((error) => {
          console.error("Ошибка загрузки регионов:", error);
        });
    }
  }

  const onMarkerClick = (e) => {
    if (!mapRef.current) return;

    const newZoom = mapRef.current.getZoom() + 5;
    const currentPosition = e.get("target").geometry.getCoordinates();
    mapRef.current.setCenter(currentPosition, newZoom);
  };

  const onTaskStateClick = (id, props) => {
    navigate(`${PATHS.MAGAZINES_EVENTS}?facilityName=${id}&status=${props}`);
  };

  const objectManagerFeatures = useMemo(() => {
    if (!facilitiesMapElements || facilitiesMapElements.length === 0) {
      return []; // Возвращаем пустой массив, если нет данных
    }

    return facilitiesMapElements
      .filter((item) => item?.coordinates)
      .map((item) => {
        return getCustomPlacemark(
          item?.id,
          [item?.coordinates?.latitude, item?.coordinates?.longitude],
          onMarkerClick,
          item?.name,
          item?.messageStatistics,
          15,
          popUpHandler,
          onTaskStateClick
        );
      });
  }, [facilitiesMapElements]);

  const MyIconContentLayout = useMemo(() => {
    return window.ymaps?.templateLayoutFactory?.createClass(
      '<div style="font-family:Inter;color:#fff;margin-top:5px;font-weight:bold;font-size:16px">' +
        "{{ properties.geoObjects.length }}" +
        "</div>"
    );
  }, [window.ymaps]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <ObjectsHeader />
      <Box sx={{ display: "flex", gap: "16px" }}>
        <Analitics />
        <Box sx={{ boxSizing: "border-box", borderRadius: "16px" }}>
          <YMaps query={YANDEX_MAPS_DATA.query}>
            {/* Отображаем карту всегда, не зависимо от наличия данных */}
            <Map
              modules={YANDEX_MAPS_DATA.modules}
              defaultOptions={YANDEX_MAPS_DATA.defaultOptions}
              instanceRef={mapRef}
              state={mapState}
              onLoad={getRegions}
              width="calc(100vw - 452px)"
              height="calc(100vh - 72px - 32px - 112px - 16px - 32px)"
            >
              <SearchControl
                options={{
                  float: "left",
                  placeholderContent: "Введите адрес объекта",
                }}
              />
              <ZoomControl options={{ float: "right" }} />

              {/* Отображаем ObjectManager только если есть объекты */}
              {objectManagerFeatures.length > 0 && (
                <ObjectManager
                  options={{
                    clusterize: true,
                    gridSize: 64,
                  }}
                  objects={{
                    openBalloonOnClick: true,
                    preset: "islands#greenDotIcon",
                  }}
                  clusters={{
                    clusterIcons: [
                      {
                        href: House,
                        size: [46, 44],
                        offset: [-20, -20],
                      },
                      {
                        href: House,
                        size: [64, 62],
                        offset: [-30, -30],
                      },
                    ],
                    clusterNumbers: [100],
                    clusterBalloonPanelMaxMapArea: 0,
                    clusterBalloonContentLayoutWidth: 350,
                    clusterBalloonContentLayoutHeight: 150,
                    clusterIconContentLayout: MyIconContentLayout,
                    clusterHideIconOnBalloonOpen: false,
                    geoObjectHideIconOnBalloonOpen: false,
                  }}
                  features={objectManagerFeatures}
                  modules={["objectManager.addon.objectsBalloon", "objectManager.addon.clustersBalloon"]}
                />
              )}
            </Map>
          </YMaps>

          {isMapPopUpOpen && (
            <MapPopUp
              selectedMarkerID={selectedMarkerID}
              setIsMapPopUpOpen={setIsMapPopUpOpen}
              setSelectedMarkerId={setSelectedMarkerId}
            />
          )}
        </Box>
      </Box>

      {!passwordDialog && (
        <SetNewPasswordDialog token={token} open={!passwordDialog} firstEntered={true} onClose={handlePasswordDialog} />
      )}
    </Box>
  );
};

export default ObjectsOnMap;
